import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Save, X, Users } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';

const AddCrewModal = ({ isOpen, onClose, onCrewAdded, targetField = null }) => {
  const [loading, setLoading] = useState(false);
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm({
    defaultValues: {
      certificate_type: 'private',
      medical_class: 'third',
      is_active: true
    }
  });

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  // Helper function to get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('authToken') || localStorage.getItem('token');
    return {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    };
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      
      const response = await axios.post('/api/crew', data, getAuthHeaders());
      
      toast.success('Crew member added successfully!');
      
      // Call the callback with the new crew data
      if (onCrewAdded) {
        onCrewAdded({
          name: data.name,
          company: data.company,
          position: data.position,
          certificate_type: data.certificate_type,
          id: response.data.id || Date.now() // fallback ID if not provided
        }, targetField);
      }
      
      // Close modal and reset form
      onClose();
      reset();
    } catch (error) {
      console.error('Error adding crew member:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
      } else if (error.response?.status === 500) {
        toast.error(`Server error: ${error.response?.data?.message || 'Internal server error'}`);
      } else if (error.response?.data?.message) {
        toast.error(`Error: ${error.response.data.message}`);
      } else {
        toast.error('Failed to add crew member');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleModalClick = (e) => {
    // Prevent closing when clicking inside the modal content
    e.stopPropagation();
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto"
        onClick={handleModalClick}
      >
        {/* Modal Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="aviation-gradient p-2 rounded-lg">
              <Users className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Add New Crew Member</h2>
              {targetField && (
                <p className="text-sm text-gray-600">
                  Adding {targetField === 'pic_crew' ? 'PIC' : 'SIC'} crew member
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={loading}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Modal Content */}
        <div className="px-6 py-4">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Basic Information */}
            <div className="card">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Basic Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    {...register('name', { required: 'Name is required' })}
                    className="input-field"
                    placeholder="e.g., John Smith"
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    {...register('email')}
                    className="input-field"
                    placeholder="john.smith@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    {...register('phone')}
                    className="input-field"
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>
            </div>

            {/* Certification Information */}
            <div className="card">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Pilot Certification</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Certificate Number
                  </label>
                  <input
                    type="text"
                    {...register('certificate_number')}
                    className="input-field"
                    placeholder="e.g., 123456789"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Certificate Type
                  </label>
                  <select {...register('certificate_type')} className="input-field">
                    <option value="student">Student Pilot</option>
                    <option value="sport">Sport Pilot</option>
                    <option value="recreational">Recreational Pilot</option>
                    <option value="private">Private Pilot</option>
                    <option value="commercial">Commercial Pilot</option>
                    <option value="airline_transport">Airline Transport Pilot (ATP)</option>
                    <option value="flight_instructor">Flight Instructor (CFI)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Medical Class
                  </label>
                  <select {...register('medical_class')} className="input-field">
                    <option value="first">First Class</option>
                    <option value="second">Second Class</option>
                    <option value="third">Third Class</option>
                    <option value="basic_med">BasicMed</option>
                    <option value="sport">Sport Pilot (No Medical)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Medical Expiry Date
                  </label>
                  <input
                    type="date"
                    {...register('medical_expiry')}
                    className="input-field"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Total Experience Hours
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    {...register('experience_hours', {
                      min: { value: 0, message: 'Experience hours cannot be negative' }
                    })}
                    className="input-field"
                    placeholder="e.g., 1500.5"
                  />
                  {errors.experience_hours && (
                    <p className="mt-1 text-sm text-red-600">{errors.experience_hours.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ratings (comma-separated)
                  </label>
                  <input
                    type="text"
                    {...register('ratings')}
                    className="input-field"
                    placeholder="e.g., Instrument, Multi-Engine, CFI"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Separate multiple ratings with commas
                  </p>
                </div>
              </div>
            </div>

            {/* Employment Information */}
            <div className="card">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Employment Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company/Organization
                  </label>
                  <input
                    type="text"
                    {...register('company')}
                    className="input-field"
                    placeholder="e.g., Delta Airlines, Local Flight School"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Position/Role
                  </label>
                  <input
                    type="text"
                    {...register('position')}
                    className="input-field"
                    placeholder="e.g., Captain, First Officer, Flight Instructor"
                  />
                </div>
              </div>
            </div>

            {/* Additional Information */}
            <div className="card">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Additional Information</h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes
                </label>
                <textarea
                  {...register('notes')}
                  rows={3}
                  className="input-field"
                  placeholder="Additional notes about this crew member, specialties, preferences, etc."
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-end space-x-4 -mx-6 -mb-4">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary flex items-center space-x-2"
                disabled={loading}
              >
                <X className="h-4 w-4" />
                <span>Cancel</span>
              </button>
              <button
                type="submit"
                disabled={loading}
                className="btn-primary flex items-center space-x-2 disabled:opacity-50"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <Save className="h-4 w-4" />
                )}
                <span>{loading ? 'Saving...' : 'Save Crew Member'}</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddCrewModal;